import React, { useEffect, useRef, useState } from 'react';
import { Button } from './ui';
import './InstagramChallengeInput.css';

type Props = {
  challengeType: 'email' | 'sms' | 'totp';
  maskedContact: string;
  loading: boolean;
  error?: string;
  onSubmit: (_code: string) => void;
  onCancel: () => void;
  // Optional: only provided when a resend endpoint is available for this
  // challenge type (SMS / email checkpoint). TOTP cannot be resent.
  onResend?: () => Promise<{ sent: boolean; error?: string }>;
};

const DIGITS = 6;
const EXPIRE_SECONDS = 295; // slightly under 5-min cache TTL
const RESEND_COOLDOWN_SECONDS = 30;

export function InstagramChallengeInput({
  challengeType,
  maskedContact,
  loading,
  error,
  onSubmit,
  onCancel,
  onResend,
}: Props) {
  const [digits, setDigits] = useState<string[]>(Array(DIGITS).fill(''));
  const [secondsLeft, setSecondsLeft] = useState(EXPIRE_SECONDS);
  const [resending, setResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(RESEND_COOLDOWN_SECONDS);
  const [resendMessage, setResendMessage] = useState<
    { tone: 'success' | 'error'; text: string } | undefined
  >(undefined);
  const inputRefs = useRef<Array<HTMLInputElement | null>>(Array(DIGITS).fill(null));
  // The exact code we last handed to onSubmit. Guards against re-submitting the
  // same rejected code — the root cause of the "instantly invalid, try again"
  // loop, where a stale code lingered in the boxes and got auto-resubmitted.
  const lastSubmittedRef = useRef<string>('');

  // Countdown timer
  useEffect(() => {
    if (secondsLeft <= 0) return;
    const id = setInterval(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearInterval(id);
  }, [secondsLeft]);

  // Resend cooldown — starts ticking on mount and after each resend.
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const id = setInterval(() => setResendCooldown((s) => s - 1), 1000);
    return () => clearInterval(id);
  }, [resendCooldown]);

  // Auto-focus first box on mount
  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  // When Instagram rejects a code, wipe the boxes and refocus so the user types
  // a FRESH code into empty boxes. Without this the rejected digits stayed put,
  // and editing one of them auto-submitted a stale code that failed instantly.
  useEffect(() => {
    if (!error) return;
    setDigits(Array(DIGITS).fill(''));
    lastSubmittedRef.current = '';
    inputRefs.current[0]?.focus();
  }, [error]);

  const formatTime = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${String(sec).padStart(2, '0')}`;
  };

  const focusBox = (idx: number) => {
    inputRefs.current[Math.max(0, Math.min(DIGITS - 1, idx))]?.focus();
  };

  // Single entry point for submitting a code. Skips empty/partial codes, refuses
  // to fire the same code twice, and clears the boxes so a rejected code can
  // never linger and get re-submitted as a stale/hybrid value.
  const submitCode = (value: string) => {
    if (loading || value.length !== DIGITS) return;
    if (value === lastSubmittedRef.current) return;
    lastSubmittedRef.current = value;
    setDigits(Array(DIGITS).fill(''));
    focusBox(0);
    onSubmit(value);
  };

  const handleChange = (idx: number, value: string) => {
    const cleaned = value.replace(/\D/g, '');
    // Autofill / paste can dump the whole code into one box (autoComplete
    // "one-time-code"). Spread the digits across the boxes instead of keeping
    // only the last one.
    if (cleaned.length > 1) {
      const next = [...digits];
      for (let i = 0; i < cleaned.length && idx + i < DIGITS; i++) {
        next[idx + i] = cleaned[i];
      }
      setDigits(next);
      const code = next.join('');
      if (code.length === DIGITS) submitCode(code);
      else focusBox(Math.min(idx + cleaned.length, DIGITS - 1));
      return;
    }
    const digit = cleaned.slice(-1);
    const next = [...digits];
    next[idx] = digit;
    setDigits(next);
    if (digit && idx < DIGITS - 1) focusBox(idx + 1);
    // Auto-submit when last box filled
    if (digit && idx === DIGITS - 1) {
      const code = next.join('');
      if (code.length === DIGITS) submitCode(code);
    }
  };

  const handleKeyDown = (idx: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      if (digits[idx]) {
        const next = [...digits];
        next[idx] = '';
        setDigits(next);
      } else {
        focusBox(idx - 1);
      }
    } else if (e.key === 'ArrowLeft') {
      focusBox(idx - 1);
    } else if (e.key === 'ArrowRight') {
      focusBox(idx + 1);
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, DIGITS);
    if (!pasted) return;
    const next = [...digits];
    for (let i = 0; i < pasted.length; i++) next[i] = pasted[i];
    setDigits(next);
    focusBox(Math.min(pasted.length, DIGITS - 1));
    if (pasted.length === DIGITS) submitCode(pasted);
  };

  const handleResend = async () => {
    if (!onResend || resending || resendCooldown > 0) return;
    setResending(true);
    setResendMessage(undefined);
    try {
      const res = await onResend();
      if (res.sent) {
        setResendMessage({ tone: 'success', text: 'New code sent.' });
        setResendCooldown(RESEND_COOLDOWN_SECONDS);
      } else {
        setResendMessage({
          tone: 'error',
          text: res.error ?? 'Could not resend the code.',
        });
        // Brief cooldown anyway, to discourage hammering on errors.
        setResendCooldown(10);
      }
    } catch (err: any) {
      setResendMessage({
        tone: 'error',
        text: err?.message ?? 'Could not resend the code.',
      });
      setResendCooldown(10);
    } finally {
      setResending(false);
    }
  };

  const code = digits.join('');
  const isComplete = code.length === DIGITS;
  const expired = secondsLeft <= 0;
  const canResend = !!onResend && challengeType !== 'totp';

  const heading =
    challengeType === 'totp'
      ? 'Open your authenticator app'
      : challengeType === 'email'
        ? 'Check your email'
        : 'Check your phone';
  const icon = challengeType === 'totp' ? '🔐' : challengeType === 'email' ? '✉️' : '💬';
  const subtitle =
    challengeType === 'totp'
      ? 'Enter the 6-digit code from your authenticator app (or the SMS code Instagram just sent).'
      : challengeType === 'email'
        ? 'Instagram sent a 6-digit code to your email address'
        : 'Instagram sent a 6-digit code to your phone number';

  return (
    <div className="ig-challenge">
      <div className="ig-challenge__icon">{icon}</div>

      <div className="ig-challenge__heading">{heading}</div>

      <p className="ig-challenge__sub">
        {subtitle}
        {challengeType !== 'totp' && maskedContact ? (
          <> &nbsp;<span className="ig-challenge__contact">{maskedContact}</span></>
        ) : null}
        {challengeType !== 'totp' ? '. Enter it below to complete the connection.' : ''}
      </p>

      <div
        className={[
          'ig-challenge__boxes',
          error ? 'ig-challenge__boxes--error' : '',
          loading ? 'ig-challenge__boxes--loading' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        onPaste={handlePaste}
      >
        {digits.map((d, i) => (
          <input
            key={i}
            ref={(el) => { inputRefs.current[i] = el; }}
            className="ig-challenge__box"
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={1}
            value={d}
            disabled={loading || expired}
            autoComplete="one-time-code"
            onChange={(e) => handleChange(i, e.target.value)}
            onKeyDown={(e) => handleKeyDown(i, e)}
            onFocus={(e) => e.target.select()}
            aria-label={`Digit ${i + 1}`}
          />
        ))}
      </div>

      {error && <p className="ig-challenge__error">{error}</p>}

      {expired ? (
        <p className="ig-challenge__expired">
          Code expired. Click <strong>Cancel</strong> and start over.
        </p>
      ) : (
        <p className="ig-challenge__timer">
          Code expires in{' '}
          <span className={secondsLeft < 60 ? 'ig-challenge__timer--urgent' : ''}>
            {formatTime(secondsLeft)}
          </span>
        </p>
      )}

      {canResend && (
        <div className="ig-challenge__resend">
          <button
            type="button"
            className="ig-challenge__resend-btn"
            onClick={handleResend}
            disabled={resending || resendCooldown > 0 || loading || expired}
          >
            {resending
              ? 'Sending…'
              : resendCooldown > 0
                ? `Resend code in ${resendCooldown}s`
                : "Didn't get a code? Resend"}
          </button>
          {resendMessage && (
            <span
              className={`ig-challenge__resend-msg ig-challenge__resend-msg--${resendMessage.tone}`}
            >
              {resendMessage.text}
            </span>
          )}
        </div>
      )}

      <div className="ig-challenge__actions">
        <Button variant="secondary" size="sm" onClick={onCancel} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="primary"
          size="sm"
          loading={loading}
          disabled={!isComplete || loading || expired}
          onClick={() => submitCode(code)}
        >
          Verify
        </Button>
      </div>
    </div>
  );
}
